<template>
    <div class="w-screen h-dvh overflow-hidden">
        <div
            class="fixed top-3 sm:top-6 left-3 w-[calc(100%-1.5rem)] sm:w-auto sm:left-6 block z-50"
        >
            <div
                class="flex items-center gap-4 bg-white/70 backdrop-blur-sm shadow rounded-xl p-4 mb-4 border border-gray-100/70"
            >
                <div class="flex gap-4 flex-col sm:flex-row">
                    <a
                        href="https://www.muzeumprahy.cz/"
                        target="_blank"
                        class="shrink-0"
                    >
                        <img
                            class="h-8 sm:h-10 w-auto shrink-0"
                            src="/img/muzeum-prahy-lg-bar-rgb.png"
                        />
                    </a>
                    <a
                        href="https://iprpraha.cz/"
                        target="_blank"
                        class="shrink-0"
                    >
                        <img
                            class="h-8 sm:h-10 w-auto shrink-0"
                            src="/img/logo_ipr_praha_2025.png"
                        />
                    </a>
                </div>
                <div class="flex flex-col shrink">
                    <span class="text-lg 2xl:text-xl font-bold"
                        >Langweilův model Prahy</span
                    >
                    <span class="text-lg 2xl:text-xl font-bold"
                        >1826 &mdash; 1837</span
                    >
                </div>
            </div>
            <!-- <LayerSlider /> -->
        </div>
        <!-- <Bookmarks v-if="store.viewReady && loginStore.hasAccess" /> -->
        <Scene v-if="sourcesLoaded && loginStore.hasAccess" />
        <div
            class="absolute top-36 sm:top-32 left-3 sm:left-6 flex gap-4 items-start z-20"
        >
            <Viewpoints v-if="store.viewReady && loginStore.hasAccess" />
            <ViewpointDetail v-if="store.viewReady && loginStore.hasAccess" />
        </div>
        <OverviewMap v-if="store.viewReady && loginStore.hasAccess" />
        <!--  <SearchWidget v-if="loginStore.hasAccess" /> -->
        <WeatherSettings
            v-if="store.viewReady"
            slot="bottom-right"
        />

        <div
            class="absolute bottom-8 sm:bottom-10 right-3 sm:right-6 flex gap-2 z-20"
        >
            <ShareButton v-if="store.viewReady && loginStore.hasAccess" />
            <button
                v-if="store.viewReady && loginStore.hasAccess"
                class="cursor-pointer bg-white/70 backdrop-blur-sm shadow rounded-lg p-2 flex justify-center items-center hover:bg-gray-100 size-10"
                @click="returnToDefaultPosition"
                title="Vrátit do výchozí pozice"
            >
                <span class="esri-icon esri-icon-home text-lg!"></span>
            </button>
        </div>
        <!-- <button
            @click="loginStore.logout"
            class="absolute right-10 bottom-10 bg-white shadow rounded-lg px-4 py-2 z-50"
        >
            Logout
        </button> -->
        <div
            v-if="loginStore.logged && !loginStore.hasAccess"
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
            Do aplikace nemáte přístup.
        </div>
    </div>
</template>

<script setup lang="ts">
import Scene from "./components/Scene.vue"
import { onMounted, ref } from "vue"
import { useStore } from "./store"
import { useLogin } from "./login"
import { useUtils } from "./utils/utils"
import IdentityManager from "@arcgis/core/identity/IdentityManager"
import esriConfig from "@arcgis/core/config"

// import Bookmarks from "./components/Bookmarks.vue"
import OverviewMap from "./components/OverviewMap.vue"
import Viewpoints from "./components/Viewpoints.vue"
import ViewpointDetail from "./components/ViewpointDetail.vue"
import ShareButton from "./components/ShareButton.vue"
import WeatherSettings from "./components/WeatherSettings.vue"

const store = useStore()
const loginStore = useLogin()
const { getDefaultCamera } = useUtils()

const sourcesLoaded = ref(false)

onMounted(async () => {
    /* if (!loginStore.logged) {
        await loginStore.login()
    } */
    esriConfig.portalUrl = "https://iprpraha.maps.arcgis.com/"
    fetchSources()
})

async function fetchSources() {
    const getSources = fetch(store.sourcesPath)
    const getTokens = fetch(store.tokensPath)

    try {
        const responses = await Promise.all([getSources, getTokens])
        const sources = await responses[0].json()
        const tokens = await responses[1].json()
        processSources(sources, tokens)
    } catch (error) {
        console.log(error)
        alert("Nenačítali se zdroje.")
    }
}

/**
 * Save sources and tokens to vuex store and
 * register tokens in IdentityManager
 *
 * @param {Object} sources - key/value object of all sources available
 * @param {Object} tokens - tokens for all locked sevrers for all needed referers
 */
function processSources(
    sources: { [k: string]: string },
    tokens: { [k: string]: { [j: string]: string } },
) {
    store.sources = sources

    const server = window.location.href.split("//")[1].split(".")[0]
    if (window.location.href.includes("langweil.az.iprpraha")) {
        store.tokens = tokens["langweil-test"]

        for (const tokenServer in store.tokens) {
            IdentityManager.registerToken({
                server: tokenServer,
                token: store.tokens[tokenServer],
            })
        }
    } else if (!server.includes("localhost")) {
        store.tokens = tokens[server]

        for (const tokenServer in store.tokens) {
            IdentityManager.registerToken({
                server: tokenServer,
                token: store.tokens[tokenServer],
            })
        }
    } else {
        const token = import.meta.env.VITE_LOCALHOST_TOKEN
        console.log(token)
        IdentityManager.registerToken({
            server: "https://iprpraha.maps.arcgis.com/sharing/rest",
            token,
        })
    }
    sourcesLoaded.value = true
}

function returnToDefaultPosition() {
    const DEFAULT_CAMERA = getDefaultCamera()

    const view = window.app.view
    if (view) {
        view.goTo(DEFAULT_CAMERA, {
            duration: 200,
            easing: "ease-in-out",
        })
    }
}
</script>
<style>
@import "./style.css";

:root {
    interpolate-size: allow-keywords;
    --arcgis-view-color-focus: transparent;
}

body,
.esri-view {
    --esri-view-outline: 0;
    --calcite-font-family: "HW Nuckle", Arial, sans-serif;
    @apply font-muzeum;
}

.esri-widget {
    @apply font-dmsans;
}

:deep(.esri-popup) {
    @apply font-dmsans   overflow-hidden;
}

.esri-popup__main-container {
    @apply max-h-[40vh] shadow-lg rounded-lg overflow-hidden;
}

.esri-view-width-medium .esri-popup__main-container {
    @apply w-72;
}

.esri-slider__thumb {
    @apply border-gray-800!;
}

.esri-attribution__sources {
    display: none !important;
}
</style>
